
/**
 * Module dependencies.
 */

import { Button, Menu, MenuItem, Wrapper } from 'react-aria-menubutton';
import { ReactNode, useRef } from 'react';
import { RouterLink } from './links/router-link';
import { color, states } from 'react-components/styles';
import { ifProp, prop, switchProp, theme } from 'styled-tools';
import { typography } from './typography/typography-styles';
import styled, { css } from 'styled-components';

/**
 * `AlignDropdown` type.
 */

type AlignDropdown = 'left' | 'right';

/**
 * `ColorTheme` type.
 */

type ColorTheme = 'primary' | 'secondary' | 'transparent';

/**
 * `Size` type.
 */

type Size = 'large' | 'medium' | 'small';

/**
 * `Props` type.
 */

type Props = {
  alignDropdown?: AlignDropdown;
  children: ReactNode;
  className?: string;
  colorTheme: ColorTheme;
  isDisabled?: boolean;
  label: ReactNode;
  menuTopPosition?: string;
  onMenuToggle?: (value: boolean) => void;
  onSelection?: (value: any) => void;
  size: Size | string;
};

/**
 * Export `DropdownWrapper` styled component.
 */

export const DropdownWrapper = styled(Wrapper)`
  position: relative;
`;

/**
 * Export `DropdownButton` styled component.
 */

export const DropdownButton = styled(Button)<{ buttonSize: Size | string; colorTheme: ColorTheme }>`
  align-items: center;
  border-radius: ${theme('dimensions.borderRadius')}px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  outline-width: 0;
  position: relative;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;
  z-index: ${theme('zIndex.dropdown.button')};

  ${switchProp('colorTheme', {
    primary: css`
      background-color: ${color('blue200')};
      color: ${color('blue500')};

      &:focus,
      &:hover {
        background-color: ${color('blue400')};
      }
    `,
    secondary: css`
      background-color: ${color('gray300')};
      color: ${color('gray500')};

      &:focus,
      &:hover {
        background-color: ${color('gray400')};
      }

      &:disabled {
        background-color: ${color('gray200')};
        color: ${color('gray400')};
      }
    `,
    transparent: css`
      background-color: transparent;
      color: ${color('gray500')};

      &:focus,
      &:hover {
        color: ${color('blue500')};
      }
    `
  })}

  ${switchProp('buttonSize', {
    large: css`
      height: 36px;
      width: 36px;
    `,
    medium: css`
      height: 32px;
      width: 32px;
    `,
    small: css`
      height: 24px;
      width: 24px;
    `
  }, css`
    height: ${props => (props as unknown as { size: string }).size};
    width: ${props => (props as unknown as { size: string }).size};
  `)}

  &:focus-visible {
    outline-width: 4px;
  }

  &:-moz-focusring {
    outline-width: 1px;
  }

  &:disabled {
    pointer-events: none;
  }
`;

/**
 * Export `StyledMenu` styled component.
 */

export const StyledMenu = styled(Menu)<{ align: AlignDropdown; top?: string }>`
  background-color: ${color('white')};
  border: 1px solid ${color('gray300')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  max-height: 70vh;
  min-width: 100%;
  overflow: auto;
  padding: 0;
  position: absolute;
  top: ${prop('top', 0)};
  z-index: ${theme('zIndex.dropdown.menu')};

  ${switchProp('align', {
    left: 'left: 0;',
    right: 'right: 0;'
  })}
`;

/**
 * Export `DropdownMenuItem` styled component.
 */

export const DropdownMenuItem = styled(MenuItem).attrs<{
  disabled: boolean;
  href?: string;
  to?: string;
}>(({ disabled, href, to }) => ({
  'aria-disabled': disabled,
  tag: to && RouterLink || href && 'a' || 'div'
}))`
  ${typography.styles.textNormal}

  appearance: none;
  background-color: transparent;
  border: 0;
  color: ${color('secondary')};
  cursor: pointer;
  display: block;
  margin: 0;
  padding: 12px 16px;
  text-align: left;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;
  white-space: nowrap;

  ${ifProp('disabled', css`
    cursor: default;
    opacity: 0.5;
    pointer-events: none;
  `)}

  ${ifProp('isActive', css`
    color: ${color('blue500')};
    pointer-events: none;
  `)}

  ${states.action`
    text-decoration: none;
  `}

  ${states.interaction`
    background-color: ${color('info100')};
    color: ${color('blue500')};
  `}
`;

/**
 * Export `DropdownMenu` component.
 */

export function DropdownMenu(props: Props): JSX.Element {
  const {
    alignDropdown = 'left',
    children,
    className,
    colorTheme,
    isDisabled,
    label,
    menuTopPosition,
    onMenuToggle,
    onSelection,
    size: buttonSize
  } = props;

  const onMenuToggleRef = useRef<((value: boolean) => void) | undefined>();
  const onSelectionRef = useRef<((value: any) => void) | undefined>();

  onMenuToggleRef.current = onMenuToggle;
  onSelectionRef.current = onSelection;

  return (
    <DropdownWrapper
      className={className}
      onMenuToggle={({ isOpen }) => {
        if (onMenuToggleRef.current) {
          onMenuToggleRef.current(isOpen);
        }
      }}
      onSelection={(value: any, event: any) => {
        if (event) {
          event.stopPropagation();
        }

        if (onSelectionRef.current) {
          onSelectionRef.current(value);
        }
      }}
    >
      <DropdownButton
        buttonSize={buttonSize}
        colorTheme={colorTheme}
        disabled={isDisabled}
      >
        {label}
      </DropdownButton>

      <StyledMenu
        align={alignDropdown}
        top={menuTopPosition}
      >
        <>
          {children}
        </>
      </StyledMenu>
    </DropdownWrapper>
  );
}
